'use client';

import { useCallback, useRef, useState, type ReactNode } from 'react';
import type { LinkProps } from 'next/link';
import { useTranslations } from 'next-intl';
import CustomLink from 'components/shared/CustomLink';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import styles from './OtherProductsCarousel.module.css';

type Props = {
  /** One label per card, for the dots. */
  titles: string[];
  /** "Visit store" pill under the row; omitted until /shop cards honour affiliate codes. */
  storeHref?: LinkProps['href'];
  children: ReactNode;
};

const ArrowIcon = ({ dir }: { dir: 'prev' | 'next' }) => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
    <path
      d={dir === 'prev' ? 'M20 12H4m6-6-6 6 6 6' : 'M4 12h16m-6-6 6 6-6 6'}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Prototype "Other products": a swipe carousel on every width. The dots follow
// the scroll, the arrows (desktop) step one card, and all controls go away when
// every card already fits in the row.
export const OtherProductsCarousel = ({ titles, storeHref, children }: Props) => {
  const t = useTranslations('invite');
  const trackRef = useRef<HTMLDivElement>(null);
  const [fits, setFits] = useState(false);
  const [active, setActive] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const count = titles.length;

  const cards = () =>
    Array.from(trackRef.current?.children ?? []).filter(
      (el): el is HTMLElement => el.tagName === 'ARTICLE',
    );
  const pos = (card: HTMLElement) => {
    const track = trackRef.current!;
    const pad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
    return (
      card.getBoundingClientRect().left - track.getBoundingClientRect().left - pad
    );
  };
  const current = () => {
    let best = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    cards().forEach((card, index) => {
      const distance = Math.abs(pos(card));
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  };
  const go = (index: number) => {
    const track = trackRef.current;
    const list = cards();
    if (!track || list.length === 0) return;
    const target = list[Math.max(0, Math.min(list.length - 1, index))];
    track.scrollTo({ left: track.scrollLeft + pos(target), behavior: 'smooth' });
  };

  const sync = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    const end = track.scrollLeft >= max - 2;
    const progress = max > 0 ? (track.scrollLeft / max) * (count - 1) : 0;
    setActive(Math.min(count - 1, Math.floor(progress + 1e-3)));
    setAtStart(track.scrollLeft <= 2);
    setAtEnd(end);
  }, [count]);

  useIsomorphicLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const fit = () => {
      setFits(track.scrollWidth <= track.clientWidth + 2);
      sync();
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(track);
    return () => observer.disconnect();
  }, [sync, count]);

  return (
    <>
      <div className={styles.wrap}>
        <div
          ref={trackRef}
          className={fits ? `${styles.track} ${styles.noScroll}` : styles.track}
          onScroll={sync}
        >
          {children}
        </div>
        {!fits ? (
          <>
            <span
              className={`${styles.edge} ${styles.edgeL}${atStart ? ` ${styles.isOff}` : ''}`}
              aria-hidden="true"
            />
            <span
              className={`${styles.edge} ${styles.edgeR}${atEnd ? ` ${styles.isOff}` : ''}`}
              aria-hidden="true"
            />
            <button
              type="button"
              className={`${styles.arrow} ${styles.arrowPrev}${atStart ? ` ${styles.isOff}` : ''}`}
              aria-label={t('carousel.previous')}
              onClick={() => go(current() - 1)}
            >
              <ArrowIcon dir="prev" />
            </button>
            <button
              type="button"
              className={`${styles.arrow} ${styles.arrowNext}${atEnd ? ` ${styles.isOff}` : ''}`}
              aria-label={t('carousel.next')}
              onClick={() => go(current() + 1)}
            >
              <ArrowIcon dir="next" />
            </button>
          </>
        ) : null}
      </div>
      {!fits ? (
        <div className={styles.moreNav}>
          {/* Same dots as the homepage "PRO beyond measure" carousel: thin pills, the active one stretched and filled. */}
          <div className={styles.dots} role="group" aria-label={t('otherProducts')}>
            {titles.map((title, index) => (
              <button
                key={title}
                type="button"
                className={index === active ? `${styles.dot} ${styles.dotActive}` : styles.dot}
                aria-label={title}
                aria-current={index === active ? 'true' : undefined}
                onClick={() => go(index)}
              >
                <span className={styles.dotFill} />
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {storeHref ? (
        <CustomLink href={storeHref} className={styles.visitStore}>
          {t('visitStore')}
        </CustomLink>
      ) : null}
    </>
  );
};
